import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as assetService from "../services/assetService";
import { useAuth } from "../context/AuthContext";
import "../styles/AssetDetail.css";

const AssetDetail = () => {
  const { id, assetCode } = useParams();
  const assetIdentifier = id || assetCode;
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [asset, setAsset] = useState(null);
  const [propertyDefinitions, setPropertyDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAsset();
  }, [assetIdentifier]);

  const loadAsset = async () => {
    try {
      const data = await assetService.getAssetById(assetIdentifier);
      console.log("Asset data from API:", data);
      console.log("Asset code field:", data.asset_code, data.assetCode);
      setAsset(data);
      // Load property definitions to check is_public (use public endpoint to avoid redirect)
      if (data.assetTypeId) {
        try {
          const props = await assetService.getPublicPropertiesByAssetType(data.assetTypeId);
          setPropertyDefinitions(Array.isArray(props) ? props : props.data || []);
        } catch (propErr) {
          console.error("Failed to load property definitions", propErr);
          setPropertyDefinitions([]);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load asset");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this asset?")) {
      try {
        await assetService.deleteAsset(assetIdentifier);
        navigate("/assets");
      } catch (err) {
        alert(err.response?.data?.message || "Failed to delete asset");
      }
    }
  };

  if (loading) return <div className="loading">Loading asset...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!asset) return <div className="error">Asset not found</div>;

  return (
    <div className="asset-detail-container">
      <div className="detail-header">
        <div>
          <h1>{asset.asset_code}</h1>
          <p className="asset-type">{asset.assetType?.name}</p>
        </div>
        <div className="detail-actions">
          {isAuthenticated && (
            <>
              <Link to={`/assets/${assetIdentifier}/edit`} className="btn btn-secondary">
                Edit
              </Link>
              <button onClick={handleDelete} className="btn btn-danger">
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-content">
        <section className="detail-section">
          <h2>Basic Information</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Status</label>
              <span className={`badge badge-${asset.status}`}>{asset.status}</span>
            </div>
            <div className="detail-item">
              <label>Asset Name</label>
              <p>{asset.name || asset.assetCode || "-"}</p>
            </div>
            <div className="detail-item">
              <label>Assigned To</label>
              <p>{asset.assignedTo || "-"}</p>
            </div>
            <div className="detail-item">
              <label>Location</label>
              <p>{asset.location || "-"}</p>
            </div>
            <div className="detail-item">
              <label>Created</label>
              <p>{new Date(asset.createdAt).toLocaleDateString()}</p>
            </div>
          </div>
          {asset.description && (
            <div className="detail-item full-width">
              <label>Description</label>
              <p>{asset.description}</p>
            </div>
          )}
        </section>

        <section className="detail-section">
          <h2>Branch Information</h2>
          {asset.branch ? (
            <div className="detail-grid">
              <div className="detail-item">
                <label>Branch Code</label>
                <p>{asset.branch.branch_code || "-"}</p>
              </div>
              <div className="detail-item">
                <label>Branch Name</label>
                <p>{asset.branch.branch_name || "-"}</p>
              </div>
              <div className="detail-item">
                <label>State</label>
                <p>{asset.branch.state || "-"}</p>
              </div>
              <div className="detail-item">
                <label>City</label>
                <p>{asset.branch.city || "-"}</p>
              </div>
              <div className="detail-item full-width">
                <label>Address</label>
                <p>{asset.branch.address || "-"}</p>
              </div>
            </div>
          ) : (
            <p className="branch-not-available">No branch assigned.</p>
          )}
        </section>

        {/* <section className="detail-section">
          <h2>Financial Information</h2>
          <div className="detail-grid">
            {asset.purchaseDate && (
              <div className="detail-item">
                <label>Purchase Date</label>
                <p>{new Date(asset.purchaseDate).toLocaleDateString()}</p>
              </div>
            )}
            {asset.warrantyExpiration && (
              <div className="detail-item">
                <label>Warranty Expiration</label>
                <p>{new Date(asset.warrantyExpiration).toLocaleDateString()}</p>
              </div>
            )}
            {asset.cost && (
              <div className="detail-item">
                <label>Cost</label>
                <p>${parseFloat(asset.cost).toFixed(2)}</p>
              </div>
            )}
          </div>
        </section> */}

        {Object.keys(asset.properties).length > 0 && (
          <section className="detail-section">
            <h2>Custom Properties</h2>
            <div className="detail-grid">
              {Object.entries(asset.properties)
                .filter(([key]) => {
                  // If authenticated, show all properties
                  if (isAuthenticated) return true;
                  // Otherwise, only show public properties
                  const propDef = propertyDefinitions.find((p) => p.propertyName === key || p.property_name === key || p.name === key);
                  return propDef?.is_public === true || propDef?.isPublic === true;
                })
                .map(([key, value]) => (
                  <div key={key} className="detail-item">
                    <label>{key}</label>
                    <p>{String(value)}</p>
                  </div>
                ))}
            </div>
          </section>
        )}
      </div>

      {isAuthenticated && (
        <Link to="/assets" className="btn btn-secondary">
          Back to Assets
        </Link>
      )}
    </div>
  );
};

export default AssetDetail;
